// src/App.jsx
import React from 'react';
import {
  BrowserRouter,
  useLocation,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { GoogleOAuthProvider } from '@react-oauth/google';

import Header       from './components/Header';
import Footer       from './components/Footer';
import Info         from './pages/Info';
import Contact      from './pages/Contact';
import Register     from './pages/Register';
import Bienvenida   from './pages/Bienvenida';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword  from './pages/ResetPassword';
import Login        from './pages/Login';
import Plans        from './pages/Plans';
import Subscribe    from './pages/Subscribe';
import Success      from './pages/Success';
import About        from './pages/About';

// Protege rutas que requieren sesión
function PrivateWrapper({ children }) {
  const token = localStorage.getItem('api_token');
  return token ? children : <Navigate to="/login" replace />;
}

// Animación de transición entre páginas
function PageWrapper({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
    >
      {children}
    </motion.div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <>
      <Header />

      <AnimatePresence mode="wait" initial={false}>
        <Routes location={location} key={location.pathname}>
          {/* Home pública */}
          <Route
            path="/"
            element={
              <PageWrapper>
                <Info />
              </PageWrapper>
            }
          />

          {/* Sobre nosotros pública */}
          <Route
            path="/sobre-nosotros"
            element={
              <PageWrapper>
                <About />
              </PageWrapper>
            }
          />

          {/* Registro (auto-login + Google en Register.jsx) */}
          <Route
            path="/register"
            element={
              <PageWrapper>
                <Register />
              </PageWrapper>
            }
          />

          {/* Bienvenida tras registro */}
          <Route
            path="/bienvenida"
            element={
              <PageWrapper>
                <Bienvenida />
              </PageWrapper>
            }
          />

          {/* Recuperación de contraseña */}
          <Route
            path="/forgot"
            element={
              <PageWrapper>
                <ForgotPassword />
              </PageWrapper>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PageWrapper>
                <ResetPassword />
              </PageWrapper>
            }
          />

          {/* Contacto público */}
          <Route
            path="/contact"
            element={
              <PageWrapper>
                <Contact />
              </PageWrapper>
            }
          />

          {/* Login público */}
          <Route
            path="/login"
            element={
              <PageWrapper>
                <Login />
              </PageWrapper>
            }
          />

          {/* Rutas protegidas */}
          <Route
            path="/plans"
            element={
              <PrivateWrapper>
                <PageWrapper>
                  <Plans />
                </PageWrapper>
              </PrivateWrapper>
            }
          />
          <Route
            path="/subscribe"
            element={
              <PrivateWrapper>
                <PageWrapper>
                  <Subscribe />
                </PageWrapper>
              </PrivateWrapper>
            }
          />
          <Route
            path="/success"
            element={
              <PrivateWrapper>
                <PageWrapper>
                  <Success />
                </PageWrapper>
              </PrivateWrapper>
            }
          />

          {/* Redireccionamiento inteligente */}
          <Route
            path="*"
            element={
              localStorage.getItem('api_token')
                ? <Navigate to="/plans" replace />
                : <Navigate to="/" replace />
            }
          />
        </Routes>
      </AnimatePresence>

      {/* Footer siempre visible */}
      <Footer />
    </>
  );
}

// src/App.jsx (solo parte final)
export default function App() {
  const GOOGLE_CLIENT_ID =
    process.env.REACT_APP_GOOGLE_CLIENT_ID ||
    (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_GOOGLE_CLIENT_ID) || // por si migras a Vite luego
    '';

  const AppTree = (
    <BrowserRouter>
      <AnimatedRoutes />
    </BrowserRouter>
  );

  // Evita crash si no hay client id
  if (!GOOGLE_CLIENT_ID) return AppTree;

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      {AppTree}
    </GoogleOAuthProvider>
  );
}

